/* eslint-disable react/jsx-props-no-spreading, react/prop-types */
import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import ChevronRight from '@mui/icons-material/ChevronRight';
import CloseRounded from '@mui/icons-material/CloseRounded';
import { atom, useAtom } from 'jotai';
import { useResetAtom } from 'jotai/utils';
import OutsideClickHandler from 'react-outside-click-handler';
import { useLocation, matchPath, useNavigate } from 'react-router-dom';
import { usePivots } from '@cyberint/common-apps/hooks/useBreadCrumbs';
import {
  buildDrillDownStopState,
  useWhiteLabelConfig,
  usePermissions,
  PermissionsUtils,
  Atoms,
  WL,
  isEmbedded,
  CUSTOMER_CONFIGURATION_TYPES,
  TENANT_TYPES,
  FEATURES,
  useFeatureHubData
} from '@cyberint/common-utils';
import { Icons, CustomTooltip, Toggle } from '@cyberint/dsc';
import { getMenuItems, GLOBAL_SEARCH_PAGE } from '../../constants/main-tabs';
import { t } from '../../i18n';
import OnlineHelpWidget from '../OnlineHelpWidget';
import GlobalSearchNavigation from './GlobalSearchNavigation/GlobalSearchNavigation';
import NavigationSearch, { NoResults } from './NavigationSearch/NavigationSearch';
import useNavigationSearch from './NavigationSearch/useNavigationSearch';
import { UserInfoBase } from './UserInfo/UserInfo';
import * as S from './styled';

export const openNavMenuState = atom(false);

const NavMenuDepsContext = createContext(null);
const useNavMenuDeps = () => useContext(NavMenuDepsContext);

const tooltipOverrideClicksProps = {
  onPointerDown: e => {
    if (e.target instanceof Element && !e.target.classList.contains('MuiTooltip-tooltip')) {
      e.preventDefault();
    }
  }
};

const tooltipComponentsProps = { tooltip: { sx: { minWidth: '250px', backgroundColor: 'transparent' } } };

const isMatchTab = (tab, pathname) =>
  (tab?.pathPattern && matchPath(tab.pathPattern, pathname)) || (tab?.path && matchPath(tab.path, pathname));

const isSubPagesMatch = (subPages, pathname) => subPages?.some(tab => isMatchTab(tab, pathname));

const isMatchChildOrSubPage = (children, tab, pathname) => {
  const childMatch = children?.some(c => {
    const childTab = c.props.tab;
    return childTab ? isMatchTab(childTab, pathname) || isSubPagesMatch(childTab.subPages, pathname) : null;
  });
  return childMatch || isSubPagesMatch(tab?.subPages, pathname);
};

const TabLink = ({ to, title, tab, icon, isFirstLevel, children, forceExpanded }) => {
  const { whiteLabelConfig } = useWhiteLabelConfig();

  const _title = title || tab?.title;
  const TitleComponent = tab?.titleComponent;
  const iconOrFunc = tab?.icon || icon;
  const IconComponent = typeof iconOrFunc === 'function' ? iconOrFunc() : iconOrFunc;
  const [isExpanded, setExpanded] = useState(false);

  const { pathname } = useLocation();
  const { pivots } = usePivots();
  const [isOpen, setOpen] = useAtom(openNavMenuState);

  // Sync with search: expand all during search, restore to collapsed when search clears
  useEffect(() => {
    if (forceExpanded) setExpanded(true);
    else setExpanded(false);
  }, [forceExpanded]);

  const {
    colors: { newBadgeBg }
  } = whiteLabelConfig;

  const cName = useMemo(() => {
    if (pivots?.length > 1) {
      const actualPathname = pivots[0].pathname;
      const match = (tab && isMatchTab(tab, actualPathname)) || isMatchChildOrSubPage(children, tab, actualPathname);
      // return non-pivot className so that in case drill down is detected, the default highlighting of Link/route will not be active
      return match ? 'active pivot' : 'none-pivot';
    }
    return isMatchChildOrSubPage(children, tab, pathname) ? 'active parent-menu' : undefined;
  }, [pathname, pivots]);

  const clickItem = e => {
    if (children?.length > 0) {
      if (isOpen) {
        e.stopPropagation();
        e.preventDefault();
        setExpanded(!isExpanded);
      }
    } else {
      setOpen(false);
    }
  };

  const itemId = `nav-item-${tab?.type || _title}`;
  const linkPath = to || tab?.path || children?.[0]?.props?.tab?.path || children?.[0]?.props?.to;
  const showBadgeOnParent = !isOpen || !isExpanded;
  const badgeType =
    tab?.badgeType ||
    (showBadgeOnParent &&
      children?.filter(({ props }) => props?.tab?.badgeType)?.map(({ props }) => props?.tab?.badgeType)?.[0]);
  return (
    <S.LinkWrapper>
      <S.LinkAndExpand>
        <S.NavTooltip
          PopperProps={tooltipOverrideClicksProps}
          componentsProps={tooltipComponentsProps}
          placement="right-start"
          title={
            isFirstLevel &&
            !isOpen &&
            (children ? (
              <S.NavSubLinksTooltip>
                <S.NavMenuLabel id="label" data-text={_title}>
                  {TitleComponent ? <TitleComponent /> : <span>{_title}</span>}
                </S.NavMenuLabel>
                {children}
              </S.NavSubLinksTooltip>
            ) : (
              <S.NavSubLinksTooltip $single>
                <S.NavMenuLink
                  to={linkPath}
                  state={buildDrillDownStopState()}
                  className={cName}
                  onClick={clickItem}
                  data-testid={itemId}>
                  <S.NavMenuLabel id="label" data-text={_title}>
                    {TitleComponent ? <TitleComponent /> : <span>{_title}</span>}
                  </S.NavMenuLabel>
                  {badgeType && (
                    <S.Badge $type={badgeType} $newBadgeBg={newBadgeBg}>
                      {t(`tabs.${badgeType}Badge`)}
                    </S.Badge>
                  )}
                </S.NavMenuLink>
              </S.NavSubLinksTooltip>
            ))
          }>
          <S.NavMenuLink
            to={linkPath}
            state={buildDrillDownStopState()}
            className={cName}
            onClick={clickItem}
            data-testid={itemId}>
            <S.ActiveMenuStripe />
            {IconComponent && (
              <S.NavMenuIconContainer>
                <S.NavMenuIcon>
                  <IconComponent size={22} />
                </S.NavMenuIcon>
              </S.NavMenuIconContainer>
            )}
            <S.NavMenuLabel id="label" data-text={_title}>
              {TitleComponent ? <TitleComponent /> : <span>{_title}</span>}
            </S.NavMenuLabel>
            {badgeType &&
              (!isOpen && isFirstLevel ? (
                <S.Badge $type={badgeType} $small $newBadgeBg={newBadgeBg}>
                  {badgeType.substring(0, 1).toUpperCase()}
                </S.Badge>
              ) : (
                <S.Badge $type={badgeType} $newBadgeBg={newBadgeBg}>
                  {t(`tabs.${badgeType}Badge`)}
                </S.Badge>
              ))}
          </S.NavMenuLink>
        </S.NavTooltip>
        {children && (
          <S.NavMenuExpandContainer onClick={() => setExpanded(!isExpanded)}>
            <S.NavMenuExpand $expanded={isExpanded}>
              <ChevronRight
                fontSize="10px"
                data-testid={isExpanded ? `button-collapse-${itemId}` : `button-expand-${itemId}`}
              />
            </S.NavMenuExpand>
          </S.NavMenuExpandContainer>
        )}
      </S.LinkAndExpand>
      {children && <S.NavSubLinks $visible={isExpanded && isOpen}>{children}</S.NavSubLinks>}
    </S.LinkWrapper>
  );
};

const NavigationMenuInner = () => {
  const {
    activeTenants,
    userInfo,
    currentCustomer,
    selectedTenant,
    customers,
    tabs,
    selectTab,
    onSignOut,
    checkAccessToTab,
    fetchCustomerConfiguration,
    NEW,
    TenantSelector,
    BackgroundOperationsBar
  } = useNavMenuDeps();

  const [navMenuFeatures, setNavMenuFeatures] = useAtom(Atoms.navMenuFeatures);
  const resetNavMenuFeatures = useResetAtom(Atoms.navMenuFeatures);
  const [isOpen, setOpen] = useAtom(openNavMenuState);
  const { whiteLabelConfig, isWhiteLabeledCompany } = useWhiteLabelConfig();
  const tenants = activeTenants || [];
  const showTenants = !isEmbedded || tenants?.length > 1;

  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { permissions, resetPermissions } = usePermissions(currentCustomer, userInfo);
  const { flags } = useFeatureHubData();
  const { tenant_type: loggedInTenantType } = userInfo.customer;

  const menuItems = useMemo(
    () => getMenuItems({ user: userInfo, selectedTenant, customers, permissions, flags, navMenuFeatures }),
    [selectedTenant, userInfo, customers, permissions, flags, navMenuFeatures]
  );
  const [searchTerm, setSearchTerm] = useState('');
  const { filteredItems, isSearching, hasResults, matchCount, totalCount } = useNavigationSearch(menuItems, searchTerm);
  const [litmosEnabled, setLitmosEnabled] = useState(false);

  const {
    logos: { MainLogo, SmallLogo },
    colors: { newBadgeBg }
  } = whiteLabelConfig;
  const showOlhIcon = WL.showOnlineHelp(whiteLabelConfig);

  const isSearchVisible = checkAccessToTab({
    tab: GLOBAL_SEARCH_PAGE,
    user: userInfo,
    selectedTenant,
    navMenuFeatures
  });

  /** *************************************************************** * */

  useEffect(() => {
    const type = pathname.replace('/', '');
    const selectedTab = tabs?.find(
      tab =>
        tab.path === pathname ||
        tab.type === type ||
        pathname.startsWith(tab.path) ||
        isSubPagesMatch(tab.subPages, pathname)
    );
    selectTab(selectedTab || {});
  }, [pathname, tabs]);

  useEffect(() => {
    if (!selectedTenant) return;

    const onUseEffect = async () => {
      try {
        setLitmosEnabled(false);

        const res = await fetchCustomerConfiguration({
          customerId: selectedTenant._id,
          configurationType: CUSTOMER_CONFIGURATION_TYPES.ELEARNING_PLATFORMS
        });
        const { customer_configurations: configs } = res.data.data;
        // TODO: itzik remove tenant_type check once migration is done
        const isEnabled = configs[0].data.litmos_enabled;

        setLitmosEnabled(
          typeof isEnabled === 'boolean'
            ? isEnabled
            : [TENANT_TYPES.CYBERINT_CUSTOMER, TENANT_TYPES.CYBERINT_MANAGEMENT].includes(loggedInTenantType)
        );
      } catch (e) {
        // do nothing
      }
    };

    onUseEffect();

    resetNavMenuFeatures();
  }, [selectedTenant]);

  const signOutHandler = () => {
    onSignOut();
    resetPermissions();
  };

  const closeMenu = useCallback(() => setOpen(false), []);
  const openMenu = useCallback(() => setOpen(true), []);
  const navigateHome = useCallback(() => navigate('/'), [navigate]);

  const getLogo = () => {
    if (isEmbedded) return null;

    return isOpen ? (
      isWhiteLabeledCompany ? (
        <S.LogoCpWrapper>
          <S.LogoWrapper onClick={navigateHome} src={`data:image/svg+xml;base64,${MainLogo}`} />
          <S.LogoTextBlock>
            <S.LogoName>{whiteLabelConfig.name}</S.LogoName>
            <S.LogoSlogan>{t('auth.slogan_ng')}</S.LogoSlogan>
          </S.LogoTextBlock>
        </S.LogoCpWrapper>
      ) : (
        <S.LogoWrapper onClick={navigateHome} src={`data:image/svg+xml;base64,${MainLogo}`} />
      )
    ) : (
      <S.LogoSmallWrapper onClick={navigateHome} src={`data:image/svg+xml;base64,${SmallLogo}`} />
    );
  };

  const buildMenuToggle = (label, ffs) => (
    <Toggle
      setValue={checked =>
        setNavMenuFeatures(prev => (checked ? [...prev, ...ffs] : prev.filter(f => !ffs.includes(f))))
      }
      value={navMenuFeatures.some(f => ffs.includes(f))}
      label={isOpen ? label : undefined}
      tooltip={isOpen ? undefined : label}
    />
  );

  return (
    <OutsideClickHandler onOutsideClick={closeMenu}>
      <S.NavMenuWrapper>
        <S.FloatingMenu
          onClick={openMenu}
          data-testid="button-floating-nav_menu"
          $isNonWhiteLabel={!isWhiteLabeledCompany}
        />
        <S.NavMenPlaceholder />
        <S.NavMenu className="nav-menu" $open={isOpen}>
          <S.CloseMenu onClick={closeMenu} data-testid="button-close-nav_menu">
            <CloseRounded />
          </S.CloseMenu>
          {getLogo()}
          {showTenants && <TenantSelector testId="input-tenants-nav_menu" />}
          <S.NavMenuToggle onClick={isOpen ? closeMenu : openMenu} $expanded={isOpen} data-testid="toggle-nav-menu" />

          <NavigationSearch
            searchTerm={searchTerm}
            onSearchChange={setSearchTerm}
            isMenuOpen={isOpen}
            isSearching={isSearching}
            matchCount={matchCount}
            totalCount={totalCount}
          />
          <S.LinksContainer>
            {isSearching && !hasResults ? (
              <NoResults>{t('common.nav_search_no_results')}</NoResults>
            ) : (
              filteredItems.map(item => (
                <TabLink key={item.tab?.type || item.title} isFirstLevel forceExpanded={isSearching} {...item}>
                  {item.subMenu?.map(sub => (
                    <TabLink key={sub.tab?.type || sub.title} {...sub} />
                  ))}
                </TabLink>
              ))
            )}
          </S.LinksContainer>
          {PermissionsUtils.isFilterNavMenu({ selectedCustomer: selectedTenant, user: userInfo }) && (
            <>
              {buildMenuToggle('TEM Only', FEATURES.TEM_FEATURES)}
              {/* buildMenuToggle('Cyclop Only', FEATURES.CAASM_FEATURES) */}
            </>
          )}
          <S.ControlsWrapper>
            {!isEmbedded && (
              <S.UserInfoWrapper>
                <UserInfoBase
                  user={userInfo}
                  signOut={signOutHandler}
                  environmentCustomerId={userInfo?.customer?._id}
                  features={selectedTenant?.features}
                />
              </S.UserInfoWrapper>
            )}
            {litmosEnabled && (
              <S.ControlWrapper>
                <CustomTooltip title={t('topbar.e_learning')} placement="top">
                  <a
                    data-testid="link-elearning_platform"
                    href="https://checkpointpublic.litmoseu.com/account/Login"
                    target="_blank"
                    rel="noopener noreferrer">
                    <Icons.GraduationCap />
                    <S.Badge $newBadgeBg={newBadgeBg} $small>
                      {NEW.substring(0, 1).toUpperCase()}
                    </S.Badge>
                  </a>
                </CustomTooltip>
              </S.ControlWrapper>
            )}
            {showOlhIcon && (
              <S.ControlWrapper>
                <OnlineHelpWidget userId={userInfo?._id} />
              </S.ControlWrapper>
            )}
            {isSearchVisible && (
              <S.ControlWrapper>
                <GlobalSearchNavigation closeMenu={closeMenu} />
              </S.ControlWrapper>
            )}
            <S.ControlWrapper>
              <BackgroundOperationsBar />
            </S.ControlWrapper>
          </S.ControlsWrapper>
        </S.NavMenu>
      </S.NavMenuWrapper>
    </OutsideClickHandler>
  );
};

export const NavigationMenuBase = props => (
  <NavMenuDepsContext.Provider value={props}>
    <NavigationMenuInner />
  </NavMenuDepsContext.Provider>
);
